import React, { ChangeEvent, useEffect, useState } from 'react'
import { getRequest, patchRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { Info } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { Switch, Typography, Grid } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { EtdProviderConfigItem, ClickPostType, ClickPostErrorType } from 'src/lib/types/integrations'
import {
  parseClickPostConfig,
  parseSavedClickPostConfig,
  getInputType,
  getCharacterLimit,
  getMerchantAttributesPayload,
  defaultClickPostErrorState,
  defaultClickPostConfig,
  parseClickPostConfigForBackend,
  handleClickPostErrors
} from 'src/lib/utils/integrationsHelpers'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { setMerchantAttributes } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import { useLocale } from 'src/lib/hooks/useLocal'

const ClickPost = () => {
  const { integrations, setIntegrations } = useIntegrations()
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()
  const { temporary, setTemporaryData } = useTemporary()
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()

  const [clickPostConfig, setClickPostConfig] = useState<ClickPostType>(defaultClickPostConfig)
  const [errors, setErrors] = useState<ClickPostErrorType>(defaultClickPostErrorState)
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    fetchETDProviderData()
  }, [])

  useEffect(() => {
    if (!temporary) return
    setClickPostConfig(temporary.integrations?.clickPost)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(integrations.clickPost) !== JSON.stringify(clickPostConfig)))
  }, [integrations.clickPost, clickPostConfig])

  useEffect(() => {
    if (!integrations.clickPost) return
    setClickPostConfig(integrations.clickPost)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [integrations.clickPost, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            clickPost: clickPostConfig
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const handleDiscardSaving = () => {
    if (!integrations.clickPost) return
    setClickPostConfig(integrations.clickPost)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const fetchETDProviderData = async () => {
    try {
      const results = await Promise.allSettled([
        getRequest('/merchant-shipping-configuration', apiURI.SHIPPING_ENGINE),
        getRequest('/utility/external-etd-config-providers/CLICKPOST/config', apiURI.SHIPPING_ENGINE)
      ])
      results.forEach(result => {
        if (result.status === 'rejected') {
          throw new Error(result.reason)
        }
      })
      setIntegrations({
        ...integrations,
        clickPost: parseClickPostConfig(results)
      })
    } catch (error) {
      errorToast('Error fetching data')
    }
  }

  const saveClickPostConfig = async () => {
    const { errors, hasErrors } = handleClickPostErrors(clickPostConfig)
    setErrors(errors)
    if (hasErrors) return
    setLoaderProperties({ isLoaderVisible: true })
    try {
      const response: any = await patchRequest(
        '/merchant-shipping-configuration',
        apiURI.SHIPPING_ENGINE,
        parseClickPostConfigForBackend(clickPostConfig)
      )
      if (response?.error) {
        throw new Error(response?.error)
      }
      if (!clickPostConfig.enabled) {
        const payload = getMerchantAttributesPayload(merchant)
        const response: any = await setMerchantAttributes(payload, merchant?.accountId ?? '')
        setMerchantData(response?.data)
      }
      setIntegrations({
        ...integrations,
        clickPost: parseSavedClickPostConfig(response, clickPostConfig.clickPostUIFields)
      })
      successToast('Changes saved')
    } catch (e: any) {
      errorToast(e.message ?? 'Error saving merchant ETD config')
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  return (
    <>
      <LayoutContainer>
        <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>ClickPost is </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${clickPostConfig?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='ClickPostToggle'
                checked={clickPostConfig?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setClickPostConfig({ ...clickPostConfig, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>

      <LayoutContainer heading='Credentials'>
        <div className='w-full md:w-full px-2 py-3'>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-[10px] border-[1px] border-[#EFD08080] text-yellow-light-color rounded-lg w-fit px-[10px] py-2 bg-[#EFD0801A]'>
              <Info size={16} color='#C7981F' />
              <p className='text-[14px]'>{t('shipping_info')}</p>
            </div>
            {clickPostConfig.clickPostUIFields.map((item: EtdProviderConfigItem, index: number) => (
              <InputTextField
                key={item.name}
                textFieldId={index.toString()}
                label={item.label}
                isRequired={item.required}
                inputType={getInputType(item.name)}
                autoComplete='new-password'
                inputValue={clickPostConfig[item.name as keyof ClickPostType]}
                handleChange={e => {
                  setClickPostConfig({ ...clickPostConfig, [item.name]: e.target.value })
                }}
                handleFocus={() => {
                  setErrors({ ...errors, [item.name]: { error: '', errorStatus: false } })
                }}
                customErrorMessage=''
                inputProps={{
                  autoComplete: 'new-password',
                  'data-lpignore': 'true',
                  'data-form-type': 'other',
                  form: {
                    autoComplete: 'off'
                  }
                }}
                fixedCharacterLimit={getCharacterLimit(item.name)}
                errorStatus={errors[item.name as keyof typeof errors]?.errorStatus ?? false}
                errorMessage={errors[item.name as keyof typeof errors]?.error ?? ''}
              />
            ))}
          </div>
        </div>
      </LayoutContainer>

      <SaveNotification
        isVisible={settingsUpdated}
        onSave={saveClickPostConfig}
        onDiscard={handleDiscardSaving}
        className='w-full'
      />
    </>
  )
}

export default ClickPost
